import {TagsDTO} from 'src/types/tags';
import {getUserDisplayName} from './userNameFormatter';

/** Who holds a card, as every view of it shows them. */
export interface TagOwner {
    /** The holder's account type - PRIVATE_DRIVER, COMPANY, EMPLOYEE or one of the staff types. */
    type: string;
    name: string;
    /** Whose user page the name opens, when there is one. */
    userId?: number;
}

/**
 * The one reading of a card's holder that the list, its cards and the tag's own page share.
 *
 * The kind is the holder's account type. A card handed to a person is stored the same way whether that
 * person is a private driver, a manager or a company's login, and every view used to call whoever sat
 * there a private driver - and spell a login with no first or last name as "null null".
 */
export const tagOwnerOf = (tag?: TagsDTO | null): TagOwner | null => {
    if (!tag) {
        return null;
    }

    if (tag.employee) {
        return {
            type: 'EMPLOYEE',
            name: getUserDisplayName({
                userType: 'EMPLOYEE',
                employee: tag.employee,
                emailAddress: tag.employee.user?.emailAddress,
            }),
        };
    }

    if (tag.company) {
        return {
            type: 'COMPANY',
            name: getUserDisplayName({
                userType: 'COMPANY',
                company: tag.company,
                emailAddress: tag.company.user?.emailAddress,
            }),
            userId: tag.company.user?.id,
        };
    }

    // A person the card was handed to, or else the member of staff a master card was issued to.
    const holder = tag.users?.[0] ?? tag.manager;

    if (holder) {
        return {type: holder.userType, name: getUserDisplayName(holder), userId: holder.id};
    }

    return null;
};

/** The caption for a holder's kind: the owner filter's own names for customers, the account type for staff. */
export const tagOwnerLabelId = (type: string): string => {
    switch (type) {
        case 'PRIVATE_DRIVER':
            return 'privateUserTabName';
        case 'COMPANY':
            return 'companyTabForRfidDialog';
        case 'EMPLOYEE':
            return 'employee';
        default:
            // BUSINESS, CLIENT_MANAGER and the rest are keys of their own - "Business", "CRM team".
            return type;
    }
};
